import { getAiBackendUrl } from '../constants/api';
import { buildAiAuthHeaders, consumeSSEStream } from '../hooks/useSSEStream';
import type { GeneratedItemInput } from './learnerService';

// Phase 4 (core learning loop): generates practice/placement items for a
// single skill by calling ai_backend's existing math-word-problem generator
// directly - the same endpoint and generation engine used everywhere else
// in the app (client/src/Components/Resources/MathWordProblems). No new
// generator is built here; this is a thin, single-skill-scoped caller
// around /generate-math-word-problems, reusing the SSE-parsing shape from
// useMathProblems.ts but only keeping the final `complete` event's
// questions (Phase 4 doesn't render a live streaming preview - items are
// validated server-side by db_backend before ever being shown to a child).

export interface SkillForGeneration {
  code: string;
  name: string;
  gradeLevel: number;
  standards: Array<{ code: string; description: string }>;
}

/**
 * generateItemsForSkill() - calls the existing math word problem generator
 * scoped to exactly one skill's standard(s), and returns the raw generated
 * items. These are NOT yet validated - the caller must POST them to
 * db_backend's .../placement/:skillId/items or .../practice/:skillId/items
 * for answer-key validation before treating them as real items.
 */
export async function generateItemsForSkill(
  skill: SkillForGeneration,
  count: number
): Promise<GeneratedItemInput[]> {
  const standardsForRequest = skill.standards.length > 0
    ? skill.standards
    : [{ code: skill.code, description: skill.name }];

  const requestData = {
    gradeLevel: String(skill.gradeLevel),
    strand: skill.name,
    standards: standardsForRequest,
    numQuestions: count,
    language: 'en',
    questionStyle: 'STAAR',
    questionMix: 'multiple_choice',
    session_id: `phase4-${skill.code}-${Date.now()}`,
    preserve_context: false,
  };

  let generatedQuestions: any[] = [];

  await consumeSSEStream({
    url: `${getAiBackendUrl()}/generate-math-word-problems`,
    headers: buildAiAuthHeaders(),
    body: JSON.stringify(requestData),
    onEvent: (data) => {
      if (data.type === 'complete') {
        const payload = data.data as { questions?: any[] } | undefined;
        if (payload && Array.isArray(payload.questions)) {
          generatedQuestions = payload.questions;
        }
      } else if (data.type === 'error') {
        throw new Error(String(data.error));
      }
    },
  });

  return generatedQuestions.map((q) => ({
    question: q.question,
    answers: q.answers || [],
    correctAnswer: q.correctAnswer,
    explanation: q.explanation || '',
    answerType: q.answerType === 'text' ? 'text' : 'multiple_choice',
    bloomsLevel: q.bloomsLevel || 'Understanding',
  }));
}
